/**
 * Renders the Product Manual gallery + Video Tutorial gallery inside SupportPage.
 * Data is fetched from /api/content/support-manuals & /api/content/support-videos.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Download, Play, X, FileText, Filter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MANUAL_CATEGORIES = ['All', 'User Manual', 'Installation Guide', 'Catalogue'];

const ManualCard = ({ item }) => (
  <a
    href={item.url}
    target="_blank"
    rel="noreferrer"
    data-testid={`manual-card-${item.id}`}
    className="group flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-[#0a3d8f] hover:shadow-xl transition-all"
  >
    <div className="relative aspect-[4/3] bg-gray-50 overflow-hidden">
      {item.thumb ? (
        <img
          src={item.thumb}
          alt={item.title}
          loading="lazy"
          className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-400">
          <FileText size={48} />
        </div>
      )}
      <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-[#0a3d8f] text-white px-2 py-1 rounded">
        {item.category || 'Manual'}
      </span>
    </div>
    <div className="p-3 flex flex-col flex-1">
      <h4 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-2 group-hover:text-[#0a3d8f] transition-colors">
        {item.title}
      </h4>
      <div className="mt-auto inline-flex items-center gap-1.5 text-xs font-semibold text-[#0a3d8f]">
        <Download size={13} /> Unduh PDF
      </div>
    </div>
  </a>
);

const VideoCard = ({ item, onPlay }) => (
  <button
    type="button"
    onClick={() => onPlay(item)}
    data-testid={`video-card-${item.id}`}
    className="group text-left flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-[#0a3d8f] hover:shadow-xl transition-all"
  >
    <div className="relative aspect-video bg-gray-900 overflow-hidden">
      {item.thumb && (
        <img
          src={item.thumb}
          alt={item.title}
          loading="lazy"
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all"
        />
      )}
      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
        <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:bg-[#0a3d8f] group-hover:text-white transition-all">
          <Play size={22} className="ml-1" fill="currentColor" />
        </div>
      </div>
    </div>
    <div className="p-3">
      <h4 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-[#0a3d8f] transition-colors">
        {item.title}
      </h4>
    </div>
  </button>
);

export const ManualGallery = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('All');
  const [visible, setVisible] = useState(12);

  useEffect(() => {
    axios.get(`${API}/api/content/support-manuals`).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(
    () => (filter === 'All' ? items : items.filter((i) => (i.category || 'User Manual') === filter)),
    [items, filter]
  );

  if (!items.length) {
    return <p className="text-sm text-gray-500" data-testid="manual-gallery-empty">Sedang memuat manual…</p>;
  }

  return (
    <div data-testid="manual-gallery">
      <div className="flex items-center flex-wrap gap-2 mb-5">
        <Filter size={14} className="text-gray-500" />
        {MANUAL_CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => { setFilter(c); setVisible(12); }}
            data-testid={`manual-filter-${c.replace(/\s+/g, '-').toLowerCase()}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
              filter === c
                ? 'bg-[#0a3d8f] border-[#0a3d8f] text-white'
                : 'bg-white border-gray-300 text-gray-700 hover:border-[#0a3d8f] hover:text-[#0a3d8f]'
            }`}
          >
            {c}
          </button>
        ))}
        <span className="ml-auto text-xs text-gray-500">{filtered.length} dokumen</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {filtered.slice(0, visible).map((it) => (
          <ManualCard key={it.id} item={it} />
        ))}
      </div>
      {visible < filtered.length && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setVisible((v) => v + 12)}
            data-testid="manual-load-more"
            className="px-6 py-2.5 rounded-md text-sm font-semibold bg-white border border-[#0a3d8f] text-[#0a3d8f] hover:bg-[#0a3d8f] hover:text-white transition-colors"
          >
            Muat Lebih Banyak
          </button>
        </div>
      )}
    </div>
  );
};

export const VideoGallery = () => {
  const [items, setItems] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [visible, setVisible] = useState(8);

  useEffect(() => {
    axios.get(`${API}/api/content/support-videos`).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  if (!items.length) {
    return <p className="text-sm text-gray-500" data-testid="video-gallery-empty">Sedang memuat video…</p>;
  }

  return (
    <div data-testid="video-gallery">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.slice(0, visible).map((it) => (
          <VideoCard key={it.id} item={it} onPlay={setPlaying} />
        ))}
      </div>
      {visible < items.length && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setVisible((v) => v + 6)}
            data-testid="video-load-more"
            className="px-6 py-2.5 rounded-md text-sm font-semibold bg-white border border-[#0a3d8f] text-[#0a3d8f] hover:bg-[#0a3d8f] hover:text-white transition-colors"
          >
            Muat Lebih Banyak
          </button>
        </div>
      )}
      {playing && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setPlaying(null)}
          data-testid="video-modal"
        >
          <button
            type="button"
            onClick={() => setPlaying(null)}
            className="absolute top-4 right-4 text-white/90 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2"
            data-testid="video-modal-close"
            aria-label="Close video"
          >
            <X size={22} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
              <video
                key={playing.url}
                src={playing.url}
                controls
                autoPlay
                playsInline
                className="w-full h-full"
                data-testid="video-modal-player"
              />
            </div>
            <p className="mt-3 text-center text-white text-sm font-medium">{playing.title}</p>
          </div>
        </div>
      )}
    </div>
  );
};
